'use client';

import React from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useRef, useState } from 'react';
import { useLocale, useMessages, useTranslations } from 'next-intl';
import { postEnquiry } from '@/api-clients/enrollment-api-client';
import { ContactSubmission } from '@/models/IContactSubmission';
import { Link } from '@/navigation';
import { Button, ButtonStyleType } from '@into-discovery/browsewebshared';
import {
  academicYearList,
  currentYear,
  getINTOCountryList,
  getMonthOptions,
  getSubjectList,
} from '@/utils/helpers/contact-us-helpers';
import colorStyles from '@/styles/utils/variables.module.scss';
import { IContactProps } from '@/models/contact/IContactProps';
import ReadUTMQueryParameters from '@/hooks/use-localstorage.hook';
import { getMonthLength } from '@/utils/helpers/date-helpers';

type Props = {
  pageProps: IContactProps;
};

export default function ContactProvider({ pageProps }: Props) {
  const currentLocale = useLocale();
  const translate = useTranslations();
  const messages = useMessages();
  const monthList = getMonthOptions(messages);
  const subjectList = getSubjectList(useTranslations);
  const countryINTOList = getINTOCountryList(useTranslations);
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const [validated, setValidated] = useState(false);
  const [formApplied, setFormApplied] = useState(false);
  const [maxDateValidation, setMaxDateValidation] = useState(31);
  const [currentlyLocatedCountry, setCurrentlyLocatedCountry] = useState(null);

  const legalUrl = `/${currentLocale}/legal-and-privacy-policy/into`;

  const utmParams = ReadUTMQueryParameters();
  const setMaxDayValidation = () => {
    const year = yearRef.current.value;
    const month = monthRef.current.value;

    setMaxDateValidation(getMonthLength(year, month));
  };

  const handleSubmit = async (event) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity()) {
      const formData: ContactSubmission = {
        FirstName: form.elements.firstName.value,
        LastName: form.elements.lastName.value,
        DateOfBirth: new Date(
          Date.UTC(
            form.elements.birthYear.value,
            form.elements.birthMonth.value,
            form.elements.birthDay.value,
          ),
        ),
        Email: form.elements.emailAddress.value,
        MobilePhone: `${form.elements.dialCode.value}${form.elements.phoneNumber.value}`,
        ContactNotes: form.elements.contactNotes.value,
        CountryOfResidence: form.elements.countryOfResidence.value,
        CurrentlyLocatedCountry: form.elements.currentlyLocatedCountry.value,
        ChinaPIPLConsent:
          form.elements.currentlyLocatedCountry.value === 'a063000000CZoZH' ? 'Yes' : null,
        AcademicYear: form.elements.academicYear.value,
        CountryOfStudy: form.elements.countryOfStudy.value,
        SubjectToStudy:
          form.elements.subjectToStudy.value === 'default'
            ? ''
            : form.elements.subjectToStudy.value,
        GdprContactPermission: true,
        RelatedCampaign: `7013A000000ZAZwQAO`,
        OnlineEnquiry: true,
        SubmissionDate: new Date(),
        CenterOfInterest: [],
        Source: utmParams ? utmParams?.replaceAll('&', ',')?.replaceAll('=', ':') : null,
      };

      const enquiryResponse = await postEnquiry(formData);
      if (enquiryResponse.status >= 400) {
        console.error(`Something went wrong - ${enquiryResponse?.statusText}`);
      } else {
        setFormApplied(true);
      }
    }

    setValidated(true);
  };

  return (
    <>
      {!formApplied ? (
        <>
          <h1 className="mb-md-3 mb-2">{translate(`Contact.contactUsBtn`)}</h1>
          <p className="mb-md-4 mb-2">{translate(`Contact.intro`)}</p>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <Col xs="12" md="6">
                <Form.Group controlId="firstName" className="mb-3">
                  <Form.Label>{translate(`Contact.firstName`)}</Form.Label>
                  <Form.Control required type="text" />
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.required`)}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="lastName" className="mb-3">
                  <Form.Label>{translate(`Contact.lastName`)}</Form.Label>
                  <Form.Control required type="text" />
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.required`)}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="birthDay">{translate(`DatePicker.dateOfBirth`)}</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      id="birthDay"
                      required
                      placeholder={translate(`DatePicker.dayPlaceholder`)}
                      type="number"
                      min="1"
                      max={maxDateValidation}
                      aria-label="enter date of your birthday"
                    />
                    <Form.Select
                      ref={monthRef}
                      onChange={setMaxDayValidation}
                      id="birthMonth"
                      defaultValue=""
                      aria-label="select month of your birthday"
                      required
                    >
                      <option value="" disabled hidden>
                        {translate(`DatePicker.monthPlaceholder`)}
                      </option>
                      {monthList.map((option) => (
                        <option key={option.text} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      ref={yearRef}
                      onChange={setMaxDayValidation}
                      id="birthYear"
                      required
                      placeholder={translate(`DatePicker.yearPlaceholder`)}
                      type="number"
                      min={currentYear - 80}
                      max={currentYear - 10}
                      aria-label="enter year of your birthday"
                    />
                    <Form.Control.Feedback type="invalid">
                      {translate(`Contact.validation.required`)}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-3" controlId="emailAddress">
                  <Form.Label>{translate(`Contact.emailAddress`)}</Form.Label>
                  <Form.Control required pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$" type="email" />
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.invalidEmail`)}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="phoneNumber">{translate(`Contact.mobileNumber`)}</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Select
                      id="dialCode"
                      defaultValue=""
                      style={{ maxWidth: '40%' }}
                      required
                      aria-label="select your country calling code"
                    >
                      <option value="" disabled hidden></option>
                      {pageProps?.countryCallingCodes.map((option) => (
                        <option key={option.text} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      id="phoneNumber"
                      required
                      type="number"
                      min="0"
                      aria-label="enter your mobile number here without country calling code"
                    />
                    <Form.Control.Feedback type="invalid">
                      {translate(`Contact.validation.required`)}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-3" controlId="countryOfResidence">
                  <Form.Label>{translate(`Contact.whereDoYouLive`)}</Form.Label>
                  <Form.Select defaultValue="" required>
                    <option value="" disabled hidden></option>
                    {pageProps?.countryList.map((option) => (
                      <option key={option.text} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.required`)}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="currentlyLocatedCountry">
                  <Form.Label>{translate(`Contact.whatCountryCurrentlyLocatedIn`)}</Form.Label>
                  <Form.Select
                    defaultValue=""
                    required
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => setCurrentlyLocatedCountry(e.target.value)}
                  >
                    <option value="" disabled hidden></option>
                    {pageProps?.countryList.map((option) => (
                      <option key={option.text} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.required`)}
                  </Form.Control.Feedback>
                </Form.Group>
                {currentlyLocatedCountry === 'a063000000CZoZH' && (
                  <>
                    <Form.Group className="mb-3" controlId="piplCompliance">
                      <Form.Label>{translate(`Contact.piplCompliance.helpText`)}</Form.Label>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy1">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`Contact.piplCompliance.copy1`)}{' '}
                        <Link href={`/legal-and-privacy-policy/into`}>INTO`s Privacy Notice</Link>.
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">
                        {translate(`Contact.piplCompliance.error`)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy2">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`Contact.piplCompliance.copy2a`)}{' '}
                        <Link href={`/legal-and-privacy-policy/into`}>INTO`s Privacy Notice</Link>
                        {translate(`Contact.piplCompliance.copy2b`)}
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">
                        {translate(`Contact.piplCompliance.error`)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy3">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`Contact.piplCompliance.copy3a`)}
                        <Link href={`/legal-and-privacy-policy/into`}>INTO`s Privacy Notice</Link>
                        {translate(`Contact.piplCompliance.copy3b`)}
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">
                        {translate(`Contact.piplCompliance.error`)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy4">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`Contact.piplCompliance.copy4`)}
                        <Link href={`/legal-and-privacy-policy/into`}>INTO`s Privacy Notice</Link>.
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">
                        {translate(`Contact.piplCompliance.error`)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </>
                )}
                <Form.Group className="mb-3" controlId="academicYear">
                  <Form.Label>{translate(`Contact.whenStartStudying`)}</Form.Label>
                  <Form.Select defaultValue="" required>
                    <option value="" disabled hidden></option>
                    {academicYearList.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.required`)}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="subjectToStudy">
                  <Form.Label>{translate(`Contact.whichSubjectStudy`)}</Form.Label>
                  <Form.Select defaultValue="default">
                    <option value="default" disabled hidden></option>
                    {subjectList.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="countryOfStudy">
                  <Form.Label>{translate(`Contact.whereToStudy`)}</Form.Label>
                  <Form.Select defaultValue="">
                    <option value="" disabled hidden></option>
                    {countryINTOList.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="gdprContactPermission">
                  <p>
                    <strong>{translate('Contact.hearMoreFromINTOBefore')}</strong>
                  </p>
                  <Form.Check.Input type="checkbox" />
                  <Form.Check.Label className="mx-2">
                    {translate(`Contact.hearMoreFromINTO`)}
                  </Form.Check.Label>
                  <Form.Control.Feedback type="invalid">
                    {translate(`Contact.validation.gdpr`)}
                  </Form.Control.Feedback>
                  <p></p>
                  <p>
                    <strong>
                      {translate('Contact.hearMoreFromINTOAfter')}
                      <a href={legalUrl}>{translate('Contact.hearMoreFromINTOAfterHere')}</a>
                      {translate('Contact.hearMoreFromINTOAfterDot')}
                    </strong>
                  </p>
                </Form.Group>
                <Form.Group className="mb-3" controlId="contactNotes">
                  <Form.Label>{translate(`Contact.howCanWeHelp`)}</Form.Label>
                  <Form.Control
                    className="h-auto"
                    rows={5}
                    maxLength={250}
                    as="textarea"
                    placeholder={translate(`Contact.helpPlaceholder`)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Button
              activeBackgroundColor={colorStyles.primaryActive}
              backgroundColor={colorStyles.primary}
              outlineColor={colorStyles.yellow}
              copyColor={colorStyles.white}
              hoverBackgroundColor={colorStyles.primaryHover}
              buttonAriaLabel="Submit contact us form"
              buttonID="contact-us"
              buttonName="contact-us"
              buttonRole="button"
              buttonStyle={ButtonStyleType.Filled}
              buttonType="submit"
            >
              {translate(`Contact.contactUsBtn`)}
            </Button>
          </Form>
        </>
      ) : (
        <div>
          <h3 className="mb-md-3 mb-2">{translate(`Contact.thankYouMessage`)}</h3>
          <hr />
          <Button
            activeBackgroundColor={colorStyles.primaryActive}
            backgroundColor={colorStyles.primary}
            outlineColor={colorStyles.yellow}
            copyColor={colorStyles.white}
            hoverBackgroundColor={colorStyles.primaryHover}
            buttonAriaLabel={translate('Contact.backToHomeBtn')}
            buttonID="back-to-home"
            buttonName="mack-to-home"
            buttonRole="link"
            buttonStyle={ButtonStyleType.Filled}
            buttonURL={`/${currentLocale}`}
          >
            {translate(`Contact.backToHomeBtn`)}
          </Button>
        </div>
      )}
    </>
  );
}
